import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button, Input, Alert, Logo } from '@heroui/react';
import { useAuthStore } from '../../stores/authStore';
import { useUiStore } from '../../stores/uiStore';

const registerSchema = z
  .object({
    firstName: z.string().min(1, 'Required'),
    lastName: z.string().min(1, 'Required'),
    orgName: z.string().min(1, 'Business name is required'),
    email: z.string().email('Enter a valid email address'),
    password: z
      .string()
      .min(12, 'At least 12 characters')
      .regex(/[A-Z]/, 'Needs an uppercase letter')
      .regex(/[a-z]/, 'Needs a lowercase letter')
      .regex(/\d/, 'Needs a number')
      .regex(/[@$!%*?&#^]/, 'Needs a symbol'),
    confirmPassword: z.string(),
    phone: z.string().optional(),
  })
  .refine((d) => d.password === d.confirmPassword, {
    path: ['confirmPassword'],
    message: 'Passwords do not match',
  });

type RegisterFormData = z.infer<typeof registerSchema>;

export default function RegisterPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const register = useAuthStore((s) => s.register);
  const theme = useUiStore((s) => s.resolvedTheme);
  const [error, setError] = useState<string | null>(null);

  // Phase 36.4 — magic-link signup token. Populated when the user
  // arrived via a sales-driven email; absent on the open-signup path.
  // The token round-trips to AuthService.register which validates +
  // atomically redeems it before creating the org. Any mismatch (orgName
  // vs. token-bound orgName, expired, already-redeemed) surfaces as a
  // backend 400 in the catch block below.
  const signupToken = searchParams.get('token')?.trim() || null;

  const {
    register: registerField,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
  });

  const onSubmit = async (data: RegisterFormData) => {
    setError(null);
    try {
      await register({
        orgName: data.orgName.trim(),
        email: data.email.trim(),
        password: data.password,
        firstName: data.firstName.trim(),
        lastName: data.lastName.trim(),
        phone: data.phone?.trim() || undefined,
        signupToken: signupToken ?? undefined,
      });
      // Mark onboarding as pending so the wizard opens on the Command Center.
      localStorage.setItem('onboarding-state', JSON.stringify({ started: true, step: 0 }));
      navigate('/');
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string | string[] } } }).response?.data
              ?.message
          : null;
      setError(
        Array.isArray(message)
          ? message.join(' · ')
          : message || 'Could not create your account. Please try again.',
      );
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-default-50">
      <div className="w-full max-w-md space-y-6 p-6">
        <div className="flex flex-col items-center space-y-3">
          <Logo variant="stacked" size={140} onDark={theme === 'dark'} />
          <h1 className="text-default-500 text-sm text-center font-normal">
            Create your account and start running your business in minutes.
          </h1>
        </div>

        {signupToken && (
          <Alert status="accent">
            You&apos;re registering through a sales-team invite. Use the
            email + business name your account manager has on file — we&apos;ll
            match the invite to your account when you submit.
          </Alert>
        )}

        {error && <Alert status="danger">{error}</Alert>}

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Input
                placeholder="First name"
                autoComplete="given-name"
                autoFocus
                fullWidth
                {...registerField('firstName')}
              />
              {errors.firstName && (
                <p className="text-danger text-xs">{errors.firstName.message}</p>
              )}
            </div>
            <div className="space-y-1">
              <Input
                placeholder="Last name"
                autoComplete="family-name"
                fullWidth
                {...registerField('lastName')}
              />
              {errors.lastName && (
                <p className="text-danger text-xs">{errors.lastName.message}</p>
              )}
            </div>
          </div>

          <div className="space-y-1">
            <Input
              placeholder="Business name"
              autoComplete="organization"
              fullWidth
              {...registerField('orgName')}
            />
            {errors.orgName && <p className="text-danger text-xs">{errors.orgName.message}</p>}
          </div>

          <div className="space-y-1">
            <Input
              type="email"
              placeholder="Work email"
              autoComplete="email"
              fullWidth
              {...registerField('email')}
            />
            {errors.email && <p className="text-danger text-xs">{errors.email.message}</p>}
          </div>

          <div className="space-y-1">
            <Input
              type="tel"
              placeholder="Phone (optional)"
              autoComplete="tel"
              fullWidth
              {...registerField('phone')}
            />
          </div>

          <div className="space-y-1">
            <Input
              type="password"
              placeholder="Password"
              autoComplete="new-password"
              fullWidth
              {...registerField('password')}
            />
            {errors.password ? (
              <p className="text-danger text-xs">{errors.password.message}</p>
            ) : (
              <p className="text-default-400 text-xs">
                12+ chars with upper, lower, number, and symbol.
              </p>
            )}
          </div>

          <div className="space-y-1">
            <Input
              type="password"
              placeholder="Confirm password"
              autoComplete="new-password"
              fullWidth
              {...registerField('confirmPassword')}
            />
            {errors.confirmPassword && (
              <p className="text-danger text-xs">{errors.confirmPassword.message}</p>
            )}
          </div>

          <Button type="submit" style={{ width: '100%' }} isDisabled={isSubmitting}>
            {isSubmitting ? 'Creating account…' : 'Create account'}
          </Button>
        </form>

        <p className="text-center text-sm text-default-500">
          Already have an account?{' '}
          <Link to="/login" className="text-primary font-medium">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
